import { useState } from 'react';
import { Modal, Form, Button, Row, Col, Spinner as BsSpinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import { config } from '@/config/env';
import CompanySelect from '@/components/common/CompanySelect';
import * as XLSX from 'xlsx';

interface Props {
  show: boolean;
  onHide: () => void;
}

interface ClienteRicerca {
  codcliente: string;
  codazienda?: string;
  ragsociale: string;
  indirizzo?: string;
  cap?: string;
  localita?: string;
  prov?: string;
  stato?: string;
  regioncountry?: string;
  latitudine?: number | null;
  longitudine?: number | null;
  [key: string]: unknown;
}

interface RicercaResponse {
  elenco: ClienteRicerca[];
  totale: number;
}

export default function ExportGeoModal({ show, onHide }: Props) {
  const { t } = useTranslation();
  const [codazienda, setCodazienda] = useState('0');
  const [prov, setProv] = useState('');
  const [cap, setCap] = useState('');
  const [localita, setLocalita] = useState('');
  const [stato, setStato] = useState('');
  const [soloConGeo, setSoloConGeo] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleExport = async () => {
    setError('');

    if (!codazienda || codazienda === '0') {
      setError(t('exportGeo.selectCompany'));
      return;
    }

    setLoading(true);
    try {
      const data = await api.post<RicercaResponse>(
        'clienti/ricerca-clienti-azienda/' + config.SITE + '/' + codazienda + '/' + config.NOME_APP,
        { camporicerca: '' }
      );

      let clienti = data?.elenco || [];

      if (clienti.length === 0) {
        setError(t('exportGeo.noResults'));
        return;
      }

      // Filtri lato client
      const provFilter = prov.trim().toUpperCase();
      const capFilter = cap.trim();
      const localitaFilter = localita.trim().toLowerCase();
      const statoFilter = stato.trim().toUpperCase();

      if (provFilter) clienti = clienti.filter(c => (c.prov || '').toUpperCase() === provFilter);
      if (capFilter) clienti = clienti.filter(c => (c.cap || '') === capFilter);
      if (localitaFilter) clienti = clienti.filter(c => (c.localita || '').toLowerCase().includes(localitaFilter));
      if (statoFilter) clienti = clienti.filter(c => (c.stato || '').toUpperCase() === statoFilter);
      if (soloConGeo) clienti = clienti.filter(c => c.latitudine && c.longitudine && c.latitudine !== 0 && c.longitudine !== 0);

      if (clienti.length === 0) {
        setError(t('exportGeo.noResultsFiltered'));
        return;
      }

      // Genera Excel
      const rows = clienti.map(c => ({
        'Codice Cliente': c.codcliente,
        'Azienda': codazienda,
        'Ragione Sociale': c.ragsociale,
        'Indirizzo': c.indirizzo || '',
        'CAP': c.cap || '',
        'Località': c.localita || '',
        'Provincia': c.prov || '',
        'Stato': c.stato || '',
        'Regione': c.regioncountry || '',
        'Latitudine': c.latitudine ?? '',
        'Longitudine': c.longitudine ?? '',
      }));

      const ws = XLSX.utils.json_to_sheet(rows);

      ws['!cols'] = [
        { wch: 15 }, { wch: 10 }, { wch: 35 }, { wch: 30 },
        { wch: 8 }, { wch: 20 }, { wch: 6 }, { wch: 5 },
        { wch: 15 }, { wch: 12 }, { wch: 12 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Clienti Geo');

      const fileName = `clienti_coordinate_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fileName);

      onHide();
    } catch {
      setError(t('exportGeo.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCodazienda('0'); setProv(''); setCap('');
    setLocalita(''); setStato(''); setSoloConGeo(true);
    setError('');
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18 }}>{t('exportGeo.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted" style={{ fontSize: 13 }}>
          {t('exportGeo.description')}
        </p>
        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label className="text-xs">{t('labels.company')} *</Form.Label>
              <CompanySelect value={codazienda} onChange={setCodazienda} className="form-select-sm" />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="text-xs">{t('exportGeo.province')}</Form.Label>
              <Form.Control size="sm" value={prov} onChange={e => setProv(e.target.value)} placeholder="es. NA" maxLength={3} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="text-xs">{t('exportGeo.zip')}</Form.Label>
              <Form.Control size="sm" value={cap} onChange={e => setCap(e.target.value)} placeholder="es. 80100" maxLength={10} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="text-xs">{t('exportGeo.city')}</Form.Label>
              <Form.Control size="sm" value={localita} onChange={e => setLocalita(e.target.value)} placeholder="es. Napoli" />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="text-xs">{t('exportGeo.country')}</Form.Label>
              <Form.Control size="sm" value={stato} onChange={e => setStato(e.target.value)} placeholder="es. IT" maxLength={3} />
            </Form.Group>
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <Form.Check type="switch" id="soloConGeo" label={t('exportGeo.onlyWithCoords')}
              checked={soloConGeo} onChange={e => setSoloConGeo(e.target.checked)} />
          </Col>
        </Row>

        {error && <Alert variant="warning" className="mt-3 py-2" style={{ fontSize: 13 }}>{error}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={handleReset}>{t('exportGeo.resetFilters')}</Button>
        <Button variant="secondary" size="sm" onClick={onHide}>{t('actions.close')}</Button>
        <Button variant="primary" size="sm" onClick={handleExport} disabled={loading || !codazienda || codazienda === '0'}>
          {loading ? <BsSpinner animation="border" size="sm" className="me-1" /> : null}
          {t('exportGeo.exportExcel')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
